import { RefObject, useEffect, useRef } from 'react';
import type { Ref } from 'react';

import { isFunction } from '../../utils';

// A DOM node sometimes needs more than one ref pointing at it at once: a component
// wants to read `.current` itself (focus management, event listeners, ...) while
// ALSO honoring a `ref` prop its own caller passed in. `useRef()` alone only covers
// the first case — this hook creates a ref for internal use and, in an effect,
// mirrors its value into `externalRef` too (whether that's a callback ref or a ref
// object), so both stay in sync and point at the same node.
export function useMergedRef<T>(
  externalRef: Ref<T> | undefined,
): RefObject<T | null> {
  const localRef = useRef<T>(null);

  useEffect(() => {
    if (!externalRef) {
      return;
    }

    if (isFunction(externalRef)) {
      externalRef(localRef.current);
    } else {
      externalRef.current = localRef.current;
    }
  });

  return localRef;
}
